import React, { useState, useEffect } from 'react';
import cn from 'classnames';
import cogoToast from 'cogo-toast';
import _ from 'lodash';
import './BrandOnboarding.scss';

import BrandOnboardingData from './BrandOnboardingData';
import BrandOnboardingControls from './BrandOnboardingControls';
import BrandOnboardingTable from './BrandOnboardingTable';
import BrandOnboardingCreateModal from './BrandOnboardingCreateModal';
import BrandOnboardingDrawer from './BrandOnboardingDrawer';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleDown, faPlus } from '@fortawesome/pro-regular-svg-icons';

import {
  createBrandRegistrationCode as createBrandRegistrationCodeAPI,
  updateBrandRegistrationCode as updateBrandRegistrationCodeAPI
} from '../../api/registration_codes.js';
import { getAccountManagers } from '../../api/admin.js';
import {
  CLEARABLE_FIELDS,
  CLOSED_PROGRAM_FORCED_VALUES,
  EDITABLE_DURING_ONBOARDING,
  FIELD_DEFINITIONS,
  FIELD_SECTIONS
} from './brandOnboardingFieldConfig';
import { STATUS_TABS, getEditableFieldNames } from './brandOnboardingHelpers';

const BrandOnboarding = () => {
  const limit = 500;
  const [offset, setOffset] = useState(0);
  const [curSearchValue, setCurSearchValue] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  // Search always restarts pagination — keeping a Load More offset would make the server skip the first N matches.
  const updateSearchValue = value => {
    setOffset(0);
    setCurSearchValue(value);
  };
  const resetSearch = () => updateSearchValue('');

  const [activeStatusTabKey, setActiveStatusTabKey] = useState('all');
  const [collapsedSectionTitles, setCollapsedSectionTitles] = useState(
    new Set(FIELD_SECTIONS.filter(section => section.collapsible).map(section => section.title))
  );
  const [recipientFilterId, setRecipientFilterId] = useState(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [adminUsers, setAdminUsers] = useState([]);
  const [allAdmins, setAllAdmins] = useState([]);

  const [selectedBrandRegistrationCodeId, setSelectedBrandRegistrationCodeId] = useState(null);
  const [brandRegistrationCodes, setBrandRegistrationCodes] = useState(null);
  const brandRegistrationCode = brandRegistrationCodes?.find(code => code.id === selectedBrandRegistrationCodeId);

  const fetchMoreBrandRegistrationCodes = () => {
    if (isSearching) return cogoToast.error('Clear the search to load more codes.');

    // WaitlistData will handle the API call
    setOffset(offset + limit);
  };

  const updateField = (prev, idx, patch) => prev.map((f, i) => (i === idx ? { ...f, ...patch } : f));
  const applyClosedProgramLock = updated => {
    const isClosedProgramChecked = !!updated.find(f => f.name === 'isClosedProgram')?.value;
    if (!isClosedProgramChecked) return updated;
    return updated.map(f => (f.name in CLOSED_PROGRAM_FORCED_VALUES ? { ...f, value: CLOSED_PROGRAM_FORCED_VALUES[f.name], isValid: true } : f));
  };
  const changeField = (index, patch) =>
    setFields(prev => applyClosedProgramLock(updateField(prev, index, typeof patch === 'function' ? patch(prev[index]) : patch)));
  const [fields, setFields] = useState(FIELD_DEFINITIONS);

  const resetValue = f => (f.defaultValue !== undefined ? f.defaultValue : f.type === 'checkbox' ? false : '');

  const clearFields = () => {
    setFields(prev => prev.map(f => ({ ...f, value: resetValue(f), isValid: true, emptyOptionLabel: undefined, currentRecipientLabel: undefined })));
    setSelectedBrandRegistrationCodeId(null);
  };

  useEffect(() => {
    if (!brandRegistrationCode) {
      setFields(prev =>
        prev.map(f => ({ ...f, value: resetValue(f), isValid: true, emptyOptionLabel: undefined, currentRecipientLabel: undefined }))
      );
      return;
    }

    setFields(prev =>
      prev.map(f => {
        const fieldValue = brandRegistrationCode[f.name] ?? '';
        // Brand columns share names with code fields — anything editable must read the code row the save writes to.
        const brandValue = EDITABLE_DURING_ONBOARDING.has(f.name) ? undefined : brandRegistrationCode.brand?.[f.name];
        const hydrated =
          f.type === 'checkbox'
            ? { ...f, value: fieldValue ? true : false, isValid: f.isFieldValid(fieldValue) }
            : { ...f, value: brandValue ?? fieldValue, isValid: f.isFieldValid(fieldValue) };

        // In the edit drawer the empty option CLEARS the recipient — only creation defaults to the creator.
        if (f.name === 'NotificationsUser_id') {
          hydrated.emptyOptionLabel = 'No personal recipient';
          hydrated.currentRecipientLabel = allAdmins.find(user => user.id === fieldValue)?.name;
        }
        return hydrated;
      })
    );
  }, [selectedBrandRegistrationCodeId]);

  // The unfiltered list is every admin in the system — name resolution only, never dropdown options.
  useEffect(() => {
    getAccountManagers('brandwaitlist', { withSlackChannel: true }).then(
      resp => {
        const users = resp.users || [];
        setAdminUsers(users);
        setFields(prev => prev.map(f => (f.name === 'NotificationsUser_id' ? { ...f, options: users } : f)));
      },
      () => {} // dropdown still offers the code-creator default if the fetch fails
    );
    getAccountManagers('brandwaitlist').then(
      resp => setAllAdmins(resp.users || []),
      () => {} // names fall back to "User {id}" if this fetch fails
    );
  }, []);

  const openCreateModal = () => {
    clearFields();
    setIsCreateModalOpen(true);
  };

  // Unsaved-edit guard — mirrors the save payload logic so only meaningful edits trigger the confirm.
  const hasUnsavedChanges = () => {
    if (isCreateModalOpen) return fields.some(field => field.value !== resetValue(field) && field.value !== '');
    if (!brandRegistrationCode) return false;

    const editableFieldNames = getEditableFieldNames(brandRegistrationCode);
    return fields.some(field => {
      if (editableFieldNames && !editableFieldNames.has(field.name)) return false;
      if (field.value === '') return CLEARABLE_FIELDS.has(field.name) && brandRegistrationCode[field.name] != null;
      const currentValue = field.type === 'number' ? Number(field.value) : field.value;
      const existingValue = field.type === 'checkbox' ? !!brandRegistrationCode[field.name] : brandRegistrationCode[field.name];
      return currentValue !== existingValue;
    });
  };

  const requestCloseOverlays = () => {
    if (hasUnsavedChanges() && !window.confirm('Discard unsaved changes?')) return;
    setIsCreateModalOpen(false);
    setSelectedBrandRegistrationCodeId(null);
  };

  // A search refetch can drop the selected code from the list — clear the orphaned id so the scroll lock can't strand the page.
  useEffect(() => {
    if (brandRegistrationCodes && selectedBrandRegistrationCodeId && !brandRegistrationCode) setSelectedBrandRegistrationCodeId(null);
  }, [brandRegistrationCodes]);

  const createBrandRegistrationCode = async () => {
    if (isLoading) return cogoToast.error('Still saving — one moment.');

    const firstErrorMessage = await validateFields();
    if (firstErrorMessage) return cogoToast.error(firstErrorMessage);

    setIsLoading(true);
    const mappedData = fields.map(field => {
      if (field.value === '') return null;
      return { [field.name]: field.value };
    });
    const fieldData = Object.assign({}, ...mappedData.filter(f => f));

    try {
      const newBrandRegistrationCode = await createBrandRegistrationCodeAPI(fieldData);
      setBrandRegistrationCodes(prev => [newBrandRegistrationCode, ...(prev || [])]);
      // A filtered view could hide the just-created code the moment its drawer closes.
      setActiveStatusTabKey('all');
      setRecipientFilterId(null);
      resetSearch();
      setIsCreateModalOpen(false);
      setSelectedBrandRegistrationCodeId(newBrandRegistrationCode.id);
      cogoToast.success('Registration code created');
    } catch (err) {
      console.error(err);
      cogoToast.error(typeof err === 'string' ? err : 'Error creating brand registration code');
    } finally {
      setIsLoading(false);
    }
  };

  const updateExistingBrandRegistrationCode = async () => {
    if (isLoading) return cogoToast.error('Still saving — one moment.');

    const editableFieldNames = getEditableFieldNames(brandRegistrationCode);
    const mappedData = fields.map(field => {
      if (editableFieldNames && !editableFieldNames.has(field.name)) return null;
      if (field.value === '') {
        const clearsStoredValue = CLEARABLE_FIELDS.has(field.name) && brandRegistrationCode[field.name] != null;
        return clearsStoredValue ? { [field.name]: null } : null;
      }
      const currentValue = field.type === 'number' ? Number(field.value) : field.value;
      const existingValue = field.type === 'checkbox' ? !!brandRegistrationCode[field.name] : brandRegistrationCode[field.name];
      if (currentValue !== existingValue) return { [field.name]: currentValue };

      return null;
    });
    const fieldData = Object.assign({}, ...mappedData.filter(f => f));
    if (_.isEmpty(fieldData)) return cogoToast.info('No changes to update');

    // Only validate the fields being written — stored values on older codes can predate current format rules.
    const firstErrorMessage = await validateFields(new Set(Object.keys(fieldData)));
    if (firstErrorMessage) return cogoToast.error(firstErrorMessage);

    setIsLoading(true);
    try {
      const updatedBrandRegistrationCode = await updateBrandRegistrationCodeAPI(brandRegistrationCode, fieldData);
      // Merge, don't replace — the PUT response has no `brand` relation, and status/progress/editability all read it.
      setBrandRegistrationCodes(prev =>
        prev.map(code => (code.id === updatedBrandRegistrationCode.id ? { ...code, ...updatedBrandRegistrationCode } : code))
      );
      cogoToast.success('Changes saved');
    } catch (err) {
      console.error(err);
      cogoToast.error(typeof err === 'string' ? err : 'Error updating brand registration code');
    } finally {
      setIsLoading(false);
    }
  };

  const validateFields = async fieldNamesToValidate => {
    let firstErrorMessage = null;
    setIsLoading(true);

    const newFields = fields.map(field => {
      const shouldValidate = !fieldNamesToValidate || fieldNamesToValidate.has(field.name);
      const isValidField = shouldValidate ? field.isFieldValid(field.value, field) : true;
      if (!isValidField) firstErrorMessage = firstErrorMessage || field.errorMessage;
      return { ...field, isValid: isValidField };
    });

    setFields(newFields);
    setIsLoading(false);
    return firstErrorMessage;
  };

  const toggleSectionCollapsed = title =>
    setCollapsedSectionTitles(prev => {
      const next = new Set(prev);
      next.has(title) ? next.delete(title) : next.add(title);
      return next;
    });

  const matchesRecipientFilter = code => {
    if (recipientFilterId === null) return true;
    if (recipientFilterId === 'unassigned') return !code.NotificationsUser_id;
    return code.NotificationsUser_id === recipientFilterId;
  };
  const ownerScopedCodes = brandRegistrationCodes?.filter(matchesRecipientFilter);

  const activeStatusTab = STATUS_TABS.find(tab => tab.key === activeStatusTabKey) || STATUS_TABS[0];
  const visibleBrandRegistrationCodes = ownerScopedCodes?.filter(activeStatusTab.matches);
  const tabCounts = {};
  STATUS_TABS.forEach(tab => (tabCounts[tab.key] = ownerScopedCodes?.filter(tab.matches).length || 0));

  const recipientNamesById = new Map(allAdmins.map(user => [user.id, user.name]));
  const allAdminIds = new Set(allAdmins.map(user => user.id));
  const recipientFilterOptionsById = new Map(adminUsers.map(user => [user.id, user.name]));
  (brandRegistrationCodes || []).forEach(code => {
    const recipientId = code.NotificationsUser_id;
    if (!recipientId) return;
    if (!recipientNamesById.has(recipientId)) recipientNamesById.set(recipientId, `User ${recipientId}`);
    if (!recipientFilterOptionsById.has(recipientId)) recipientFilterOptionsById.set(recipientId, recipientNamesById.get(recipientId));
  });
  const recipientFilterOptions = Array.from(recipientFilterOptionsById, ([id, name]) => ({ id, name }));

  const editableFieldNamesForSelected = brandRegistrationCode ? getEditableFieldNames(brandRegistrationCode) : null;
  const isFieldEditableForSelected = field => !editableFieldNamesForSelected || editableFieldNamesForSelected.has(field.name);
  const drawerHasUnsavedChanges = !!brandRegistrationCode && !isCreateModalOpen && hasUnsavedChanges();

  const emptyMessage = curSearchValue
    ? 'No registration codes match your search'
    : recipientFilterId !== null
    ? 'No codes for this owner in this view'
    : activeStatusTabKey === 'all'
    ? 'No registration codes yet — create the first one.'
    : `No codes in ${activeStatusTab.label}`;

  return (
    <div className='brand-onboarding-outer-container'>
      <div className='brand-onboarding-inner-container'>
        <BrandOnboardingData
          limit={limit}
          offset={offset}
          curSearchValue={curSearchValue}
          setIsSearching={setIsSearching}
          isSearching={isSearching}
          brandRegistrationCodes={brandRegistrationCodes}
          setBrandRegistrationCodes={setBrandRegistrationCodes}
        />

        <div className='brand-onboarding-header'>
          <div className='brand-onboarding-title-row'>
            <div className='brand-onboarding-title-container'>
              <h1>Brand Onboarding</h1>
              <div className='brand-onboarding-subtitle'>Create registration codes and track each brand through onboarding</div>
            </div>

            <button className='brand-onboarding-primary-button' onClick={openCreateModal}>
              <FontAwesomeIcon icon={faPlus} />
              New Code
            </button>
          </div>

          <div className='brand-onboarding-toolbar'>
            <div className='brand-onboarding-status-tabs'>
              {STATUS_TABS.map(tab => (
                <div
                  key={tab.key}
                  className={cn('brand-onboarding-status-tab', {
                    active: tab.key === activeStatusTabKey,
                    alert: tab.key === 'needsAttention' && tabCounts.needsAttention > 0
                  })}
                  tabIndex={0}
                  onClick={() => setActiveStatusTabKey(tab.key)}
                  onKeyDown={e => {
                    if (e.key !== 'Enter' && e.key !== ' ') return;
                    e.preventDefault();
                    setActiveStatusTabKey(tab.key);
                  }}
                >
                  {tab.label}
                  <span className='brand-onboarding-status-tab-count'>{tabCounts[tab.key]}</span>
                </div>
              ))}
            </div>

            <div className='brand-onboarding-toolbar-controls'>
              <div className='brand-onboarding-owner-filter'>
                <span className='brand-onboarding-owner-filter-label'>Owner</span>
                <div className='brand-onboarding-owner-filter-select'>
                  <FontAwesomeIcon icon={faAngleDown} className='brand-onboarding-owner-filter-icon' />
                  <select
                    value={recipientFilterId ?? ''}
                    onChange={e => {
                      const { value } = e.target;
                      setRecipientFilterId(value === '' ? null : value === 'unassigned' ? 'unassigned' : parseInt(value));
                    }}
                  >
                    <option value=''>Anyone</option>
                    <option value='unassigned'>Unassigned</option>
                    {recipientFilterOptions.map(option => (
                      <option key={option.id} value={option.id}>
                        {option.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <BrandOnboardingControls curSearchValue={curSearchValue} setCurSearchValue={updateSearchValue} resetSearch={resetSearch} />
            </div>
          </div>
        </div>

        <div className='brand-onboarding-data-container'>
          <BrandOnboardingTable
            isInitialLoad={brandRegistrationCodes === null}
            visibleBrandRegistrationCodes={visibleBrandRegistrationCodes}
            selectedBrandRegistrationCodeId={selectedBrandRegistrationCodeId}
            isSearching={isSearching}
            emptyMessage={emptyMessage}
            recipientNamesById={recipientNamesById}
            allAdminIds={allAdminIds}
            onSelectCode={setSelectedBrandRegistrationCodeId}
            onFilterByOwner={setRecipientFilterId}
            showLoadMore={brandRegistrationCodes?.length >= limit + offset}
            onLoadMore={fetchMoreBrandRegistrationCodes}
          />
        </div>

        {isCreateModalOpen && (
          <BrandOnboardingCreateModal
            fields={fields}
            onFieldChange={changeField}
            collapsedSectionTitles={collapsedSectionTitles}
            onToggleSectionCollapsed={toggleSectionCollapsed}
            recipientNamesById={recipientNamesById}
            isLoading={isLoading}
            onCreate={createBrandRegistrationCode}
            onRequestClose={requestCloseOverlays}
          />
        )}

        {!!brandRegistrationCode && !isCreateModalOpen && (
          <BrandOnboardingDrawer
            registrationCode={brandRegistrationCode}
            fields={fields}
            isFieldEditable={isFieldEditableForSelected}
            onFieldChange={changeField}
            collapsedSectionTitles={collapsedSectionTitles}
            onToggleSectionCollapsed={toggleSectionCollapsed}
            recipientNamesById={recipientNamesById}
            adminUsers={adminUsers}
            isLoading={isLoading}
            hasUnsavedChanges={drawerHasUnsavedChanges}
            onUpdate={updateExistingBrandRegistrationCode}
            onRequestClose={requestCloseOverlays}
          />
        )}
      </div>
    </div>
  );
};

BrandOnboarding.propTypes = {};

export default BrandOnboarding;
